import React, { useEffect } from 'react';
import { AuthProvider } from '@/auth/auth-provider';
import { TaskPane } from '@/components/layout/TaskPane';
import { applyTheme, getStoredTheme, watchSystemTheme, THEME_STORAGE_KEY } from '@/lib/theme';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Office Add-in] Uncaught error:', error, info);
  }

  componentDidMount() {
    // Catch unhandled async errors (Promise rejections) that ErrorBoundary can't catch
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleGlobalError);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault();
    console.error('[Office Add-in] Unhandled rejection:', event.reason);
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason || 'Unhandled async error'));
    this.setState({ error });
  };

  handleGlobalError = (event: ErrorEvent) => {
    console.error('[Office Add-in] Global error:', event.error);
    if (event.error instanceof Error) {
      this.setState({ error: event.error });
    }
  };

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 16, fontFamily: 'system-ui', fontSize: 13 }}>
          <h3 style={{ color: '#dc2626', marginBottom: 8 }}>Something went wrong</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: '#666', marginBottom: 12 }}>
            {this.state.error.message}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              padding: '6px 16px',
              fontSize: 12,
              border: '1px solid #ccc',
              borderRadius: 6,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeSync />
      <AuthProvider>
        <TaskPane />
      </AuthProvider>
    </ErrorBoundary>
  );
}

function ThemeSync() {
  useEffect(() => {
    applyTheme(getStoredTheme());

    const stopWatching = watchSystemTheme(() => {
      applyTheme(getStoredTheme());
    });

    const handleStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) {
        applyTheme(getStoredTheme());
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      stopWatching();
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return null;
}
